// src/admin/Exams/ExamTimeTable/ActionsMenu.js

import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ActionsMenu = ({ row, user, handleGenerateHalltickets, handleViewDetails }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [loadingGenerate, setLoadingGenerate] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [progress, setProgress] = useState(0);
  const [rollDialogOpen, setRollDialogOpen] = useState(false);
  const [rollNumbers, setRollNumbers] = useState("");
  const [loadingGenerateSelected, setLoadingGenerateSelected] = useState(false);
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [downloadRollNumbers, setDownloadRollNumbers] = useState("");
  const [loadingDownloadSelected, setLoadingDownloadSelected] = useState(false);
  const open = Boolean(anchorEl);
  const handleGenerateSelected = async () => {
    if (!rollNumbers.trim()) return;

    setLoadingGenerate(true);

    try {
      await handleGenerateHalltickets(row, rollNumbers);
    } catch (err) {
      console.error("Generate selected failed:", err);
    } finally {
      setLoadingGenerate(false);
      //setRollDialogOpen(false);
      //setRollNumbers("");
      //handleClose();
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // ✅ Handle Download with Progress
  const handleDownload = async () => {
    setLoadingDownload(true);
    setProgress(0);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/exam-halltickets/download`,
        {
          clientId: user.client_id,
          collegeId: row.college_id,
          masterScheduleId: row.exam_schedule_master_id,
          detailScheduleId: row.exam_schedule_detail_id,
          branchId: row.branch_id,
        },
        {
          responseType: "blob",
          onDownloadProgress: (event) => {
            if (event.total) {
              const percent = Math.round((event.loaded * 100) / event.total);
              setProgress(percent);
            }
          },
        }
      );
      const programName = row.program_name || "Program";
      const branchName = row.branch_name || "Branch";
      const examMonth = row.exam_month || "Month";
      const examType = row.exam_type_name || "Type";

      // ✅ Replace spaces with underscores for clean filenames
      const safeProgram = programName.replace(/\s+/g, "_");
      const safeBranch = branchName.replace(/\s+/g, "_");
      const safeMonth = examMonth.replace(/\s+/g, "_");
      const safeType = examType.replace(/\s+/g, "_");

      // ✅ Build filename
      const filename = `${safeProgram}_${safeBranch}_${safeMonth}_${safeType}_halltickets.pdf`;
      // Save file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Download failed:", err);
    } finally {
      setLoadingDownload(false);
      setProgress(0);
      handleClose();
    }
  };
  const handleGenerate = async () => {
    setLoadingGenerate(true);
    try {
      await handleGenerateHalltickets(row);
    } catch (err) {
      console.error("Generate failed:", err);
    } finally {
      setLoadingGenerate(false);
      handleClose();
    }
  };
  const handleDownloadSelected = async () => {
    if (!rollNumbers.trim()) return;

    setLoadingDownloadSelected(true);
    setProgress(0);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/exam-halltickets/download-selected`,
        {
          clientId: user.client_id,
          collegeId: row.college_id,
          masterScheduleId: row.exam_schedule_master_id,
          detailScheduleId: row.exam_schedule_detail_id,
          branchId: row.branch_id,
          roll_numbers: rollNumbers
            .split(",")
            .map((r) => r.trim())
            .filter((r) => r !== ""),
        },
        {
          responseType: "blob",
          onDownloadProgress: (event) => {
            if (event.total) {
              const percent = Math.round((event.loaded * 100) / event.total);
              setProgress(percent);
            }
          },
        }
      );

      const safeProgram = row.program_name.replace(/\s+/g, "_");
      const safeBranch = row.branch_name.replace(/\s+/g, "_");
      const filename = `${safeProgram}_${safeBranch}_Selected_Halltickets.pdf`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Download selected failed:", err);
    } finally {
      setLoadingDownloadSelected(false);
      setRollDialogOpen(false);
      setRollNumbers("");
      handleClose();
    }
  };

  return (
    <>
      <IconButton onClick={handleClick} size="small">
        <MoreVertIcon />
      </IconButton>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {/* View Timetable */}
        <MenuItem
          onClick={() => {
            handleViewDetails(row);
            handleClose();
          }}
          sx={{ fontSize: "0.8rem" }}
        >
          <ListItemIcon>
            <ScheduleIcon fontSize="small" sx={{ color: "blue" }} />
          </ListItemIcon>
          <ListItemText primary="View Timetable" primaryTypographyProps={{ fontSize: "0.9rem" }} />
        </MenuItem>

        {/* Generate Halltickets */}
        <MenuItem onClick={handleGenerate} sx={{ fontSize: "0.8rem" }}>
          <ListItemIcon>
            <PrintIcon fontSize="small" sx={{ color: "green" }} />
          </ListItemIcon>
          <ListItemText
            primary="Generate Halltickets"
            primaryTypographyProps={{ fontSize: "0.9rem" }}
          />
        </MenuItem>

        {/* Download Halltickets */}
        <MenuItem onClick={handleDownload} sx={{ fontSize: "0.8rem" }}>
          <ListItemIcon>
            <DownloadIcon fontSize="small" sx={{ color: "orange" }} />
          </ListItemIcon>
          <ListItemText
            primary="Download Halltickets"
            primaryTypographyProps={{ fontSize: "0.9rem" }}
          />
        </MenuItem>
        {/* Generate Selected Halltickets */}
        <MenuItem
          onClick={() => {
            setRollDialogOpen(true);
          }}
          sx={{ fontSize: "0.8rem" }}
        >
          <ListItemIcon>
            <PrintIcon fontSize="small" sx={{ color: "purple" }} />
          </ListItemIcon>
          <ListItemText
            primary="Generate Selected Halltickets"
            primaryTypographyProps={{ fontSize: "0.9rem" }}
          />
        </MenuItem>
      </Menu>

      {/* Progress Dialog */}
      {/* Progress Dialog for Generate */}
      <Dialog open={loadingGenerate} onClose={() => setLoadingGenerate(false)}>
        <DialogTitle>Generating Halltickets...</DialogTitle>
        <DialogContent>
          <Box sx={{ width: "300px" }}>
            <LinearProgress variant="indeterminate" />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Progress Dialog for Download */}
      <Dialog open={loadingDownload} onClose={() => setLoadingDownload(false)}>
        <DialogTitle>Downloading Halltickets...</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {progress > 0 ? `${progress}% completed` : "Starting download..."}
          </Typography>
          <Box sx={{ width: "300px" }}>
            <LinearProgress
              variant={progress > 0 ? "determinate" : "indeterminate"}
              value={progress}
            />
          </Box>
        </DialogContent>
      </Dialog>
      {/* Generate Selected Dialog */}
      <Dialog
        open={rollDialogOpen}
        onClose={() => setRollDialogOpen(false)}
        maxWidth="md" // 🔥 change to md or lg
        fullWidth
      >
        <DialogTitle>Halltickets (Selected Students)</DialogTitle>

        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Enter comma separated Student Roll Numbers
          </Typography>

          <TextField
            fullWidth
            multiline
            minRows={3}
            placeholder="Example: 21CS001,21CS002,21CS010"
            value={rollNumbers}
            onChange={(e) => setRollNumbers(e.target.value)}
            sx={{
              "& .MuiInputBase-input": {
                fontSize: "18px", // 🔥 Change this value
              },
            }}
          />
        </DialogContent>

        <DialogActions sx={{ gap: 1, pr: 3, pb: 2 }}>
          <Button onClick={() => setRollDialogOpen(false)}>Cancel</Button>

          {/* Generate Button */}
          <Button
            onClick={handleGenerateSelected}
            variant="contained"
            disabled={loadingGenerateSelected}
            sx={{
              backgroundColor: "#1565c0",
              color: "#fff",
              "&:hover": {
                backgroundColor: "#0d47a1",
              },
            }}
          >
            Generate
          </Button>

          {/* Download Only */}
          <Button
            onClick={handleDownloadSelected}
            variant="contained"
            disabled={loadingDownloadSelected}
            sx={{
              backgroundColor: "#2e7d32",
              color: "#fff",
              "&:hover": { backgroundColor: "#1b5e20" },
            }}
          >
            Download
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ActionsMenu.propTypes = {
  row: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  handleGenerateHalltickets: PropTypes.func.isRequired,
  handleViewDetails: PropTypes.func.isRequired,
};

export default ActionsMenu;
